import React, { useState, useEffect } from 'react';
import AnnotationTool from './AnnotationTool';
import './AnnotationToolStyles.css';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import { useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Api from '../../services/Api';

/**
 * Container para a ferramenta de anotação iridológica
 * Gerencia o estado de carregamento, dados do cliente e comunicação com o backend
 */
const AnnotationToolContainer = ({ onComplete }) => {
  // Obter o idCall da URL usando useParams ou useLocation
  const params = useParams();
  const location = useLocation();
  const { t } = useTranslation();
  // Extrair idCall da URL (formato: /annotation-tool/453be19c-cf51-4ebb-b00d-73302c8a1b04)
  const getCallIdFromUrl = () => {
    // Primeiro tenta obter do useParams (se estiver configurado com react-router)
    if (params && params.idCall) {
      return params.idCall;
    }
    // Caso contrário, extrai do pathname
    const pathParts = location.pathname.split('/');
    const lastPart = pathParts[pathParts.length - 1];

    // Verifica se o último segmento da URL parece um UUID válido
    if (lastPart && lastPart.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
      return lastPart;
    }
    return null;
  };

  const idCall = getCallIdFromUrl();

  // Estado para armazenar os dados do cliente
  const [clientData, setClientData] = useState(null);
  const [call, setCall] = useState(null);

  // Estado para armazenar as imagens do cliente
  const [images, setImages] = useState([]);

  // Estado para controlar o carregamento
  const [isLoading, setIsLoading] = useState(true);

  // Estado para controlar se a análise foi salva
  const [isAnnotationSaved, setIsAnnotationSaved] = useState(false);

  // Efeito para carregar os dados do cliente e imagens
  useEffect(() => {
    if (idCall) {
      fetchCallData();
    } else {
      toast.error(t('dashboardCalls.notifications.notFound'), {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
      setIsLoading(false);
    }
  }, [idCall]);

  const fetchCallData = async () => {
    setIsLoading(true);
    try {
      const response = await Api.get(`/get-call/${idCall}`);
      console.log(response);
      if (response && response?._id) {
        setCall(response);
      } else {
        toast.error(t('dashboardCalls.notifications.notFound'), {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
      }
    } catch (error) {
      console.log('error', error);
      toast.error(t('dashboardCalls.notifications.notFound'), {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Função para salvar as anotações
  const handleSaveAnnotation = async (annotationsData) => {
    setIsLoading(true);
    try {
      // Enviar as anotações para o backend
      const response = await Api.post("/analysis/annotations/save", {
        idCall,
        annotations: JSON.stringify(annotationsData)
      });

      if (response.hasOwnProperty('_id') || response.hasOwnProperty('ok') || response?.success) {
        toast.success(t('evaluation.notifications.success'), {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        setIsAnnotationSaved(true);
        setCall({
          ...call,
          access_key: response?.access_key
        });
        /*if (onComplete) {
          onComplete(response);
        }*/
      } else {
        toast.error(t('evaluation.notifications.error'), {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
      }
    } catch (error) {
      console.error('Erro ao salvar as anotações:', error);
      if (error.response) {
        toast.error(error.response.data.message || 'Erro ao salvar as anotações.');
      } else {
        toast.error('Não foi possível salvar as anotações. Por favor, tente novamente.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendEmail = async () => {
    setIsLoading(true);
    try {
      // Enviar as anotações para o backend
      const response = await Api.post(`analysis/${idCall}/send-email`);
      if (response && response?.success) {
        toast.success(response?.message || t('evaluation.notifications.success'), {
          position: toast.POSITION.BOTTOM_RIGHT
        });
      } else {
        toast.error(response?.message || t('evaluation.notifications.error'), {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
      }
    } catch (error) {
      console.error('Erro ao enviar o email:', error);
      if (error.response) {
        toast.error(error.response.data.message || 'Erro ao enviar o email.');
      } else {
        toast.error('Não foi possível enviar o email. Por favor, tente novamente.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Renderizar mensagem de carregamento
  if (isLoading && !call?.customer) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Carregando dados do cliente...</p>
      </div>
    );
  }

  // Renderizar mensagem de erro se não houver dados do cliente
  if (!isLoading && !call?.customer) {
    return (
      <div className="error-container">
        <div className="error-icon">!</div>
        <h3>Erro ao carregar dados</h3>
        <p>Não foi possível carregar os dados do cliente. Por favor, verifique o ID na URL e tente novamente.</p>
        <button
          className="retry-button"
          onClick={() => window.location.reload()}
        >
          Tentar Novamente
        </button>
      </div>
    );
  }

  // Renderizar mensagem de sucesso se a análise foi salva
  if (isAnnotationSaved) {
    return (
      <div className="analysis-success">
        <div className="success-icon">✓</div>
        <h2>Análise Concluída com Sucesso!</h2>
        <p>
          A análise iridológica foi salva e o relatório será enviado ao cliente.
        </p>
        <div className="success-actions">
          <button
            className="new-analysis-button"
            onClick={() => window.location.href = '/dashboard/calls'}
          >
            Voltar para o Dashboard
          </button>
          <button
            className="view-report-button"
            onClick={() => window.open(`/analysis-report/${idCall}?access-key=${call?.access_key}`, '_blank')}
          >
            Visualizar Relatório
          </button>
        </div>
      </div>
    );
  }

  // Renderizar a ferramenta de anotação
  return (
    <>
      <AnnotationTool
        call={call}
        business={call?.business}
        user={call?.user}
        images={call?.iris}
        clientData={call?.customer}
        onSaveAnnotation={handleSaveAnnotation}
        onSendEmail={handleSendEmail}
      />
      <ToastContainer position="top-right" autoClose={5000} />
    </>
  );
};

export default AnnotationToolContainer;
